import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import SectionCard from '../../components/SectionCard';
import { getJobRowStyle, JOB_STATUS_LABELS } from '../../utils/jobStatusColor';
import { daysUntilDue } from '../../utils/dueDate';
import { useAuthStore } from '../../stores/authStore';
import { Calendar, Building2, User, Wrench, FileText, ClipboardList, Send, FileCheck } from 'lucide-react';
import SendReportsModal from './SendReportsModal';
import QuoteRequestModal from './QuoteRequestModal';
import QuoteRequestResponseModal from './QuoteRequestResponseModal';
import PageHeading from '../../components/PageHeading';

function Repairing() {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [sortKey, setSortKey] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [sendReportsRow, setSendReportsRow] = useState(null);
  const [quoteRequestRow, setQuoteRequestRow] = useState(null);
  const [respondingQuote, setRespondingQuote] = useState(null);
  const [newCase, setNewCase] = useState({
    building_id: '',
    ss_code: '',
    inspector_party: 'EXTERNAL',
    repairer_party: 'OURS',
  });
  const [section, setSection] = useState('open');
  const [reading, setReading] = useState(null);
  const queryClient = useQueryClient();

  const { data: repairsData, isLoading } = useQuery({
    queryKey: ['repairs'],
    queryFn: async () => {
      const response = await api.get('/jobs/repairs');
      return response.data?.data ?? [];
    },
  });

  const { data: myQuoteRequests } = useQuery({
    queryKey: ['quote-requests', 'mine'],
    queryFn: async () => {
      const response = await api.get('/quote-requests');
      return response.data?.data ?? [];
    },
    enabled: user?.role === 'WORKER',
  });

  const { data: caseRows } = useQuery({
    queryKey: ['repair-cases'],
    queryFn: async () => (await api.get('/repair-cases')).data?.data ?? [],
    staleTime: 0,
  });
  const { data: buildingsData } = useQuery({
    queryKey: ['buildings', 'repair-case-picker'],
    queryFn: async () => (await api.get('/buildings', { params: { limit: 500 } })).data?.data ?? [],
    enabled: user?.role !== 'WORKER',
  });

  const openCase = useMutation({
    mutationFn: async (body) => (await api.post('/repair-cases', body)).data.data,
    onSuccess: (row) => {
      queryClient.invalidateQueries({ queryKey: ['repair-cases'] });
      navigate(`/repairing/cases/${row.id}`);
    },
  });

  const repairs = Array.isArray(repairsData) ? repairsData : [];
  const cases = Array.isArray(caseRows) ? caseRows : [];
  const buildings = Array.isArray(buildingsData) ? buildingsData : [];
  const doneStatuses = new Set(['SERVICE_ISSUED', 'REINSPECTION_ISSUED', 'PAPERWORK_FILED']);
  const inProgressCases = cases.filter((row) => !doneStatuses.has(row.status));
  const doneCases = cases.filter((row) => doneStatuses.has(row.status));
  const statusLabel = {
    COLLECTING: 'Collecting',
    REPAIR_REQUESTED: 'Repair requested',
    SERVICE_ISSUED: 'Service issued',
    REINSPECTION_ISSUED: 'Re-inspection issued',
    PAPERWORK_FILED: 'Paperwork filed',
  };
  const pendingQuotes = Array.isArray(myQuoteRequests) ? myQuoteRequests.filter((q) => q.status === 'PENDING') : [];
  const sorted = [...repairs].sort((a, b) => {
    const mul = sortOrder === 'asc' ? 1 : -1;
    if (sortKey === 'date') {
      const dA = a.due_date ? new Date(a.due_date).getTime() : 0;
      const dB = b.due_date ? new Date(b.due_date).getTime() : 0;
      return mul * (dA - dB);
    }
    if (sortKey === 'building') {
      const sA = (a.building_name || '').toLowerCase();
      const sB = (b.building_name || '').toLowerCase();
      return mul * sA.localeCompare(sB);
    }
    if (sortKey === 'ss') {
      const sA = (a.ss_code || '').toLowerCase();
      const sB = (b.ss_code || '').toLowerCase();
      return mul * sA.localeCompare(sB);
    }
    return 0;
  });

  const handleRowClick = (e, row) => {
    if (e.target.closest('button')) return;
    openIssuedReports(row);
  };

  const getRowStyle = (row) => {
    return getJobRowStyle(row.status || 'FAIL', daysUntilDue(row.due_date));
  };

  if (isLoading && section === 'open') {
    return (
      <div>
        <PageHeading
          title="Repairing"
          subPages={[
            { id: 'open', label: 'Open Job', active: true, onClick: () => setSection('open') },
            { id: 'list', label: 'Job list', active: false, onClick: () => setSection('list') },
          ]}
        />
        <div className="text-center py-12">Loading...</div>
      </div>
    );
  }

  async function openIssuedReports(row) {
    setReading({ row, loading: true, reports: [] });
    try {
      let caseId = row.guide ? row.id : null;
      if (!caseId && row.job_id) {
        const opened = (
          await api.post('/repair-cases', {
            building_id: row.building_id,
            job_id: row.job_id,
            ss_code: row.ss_code,
            inspector_party: 'OURS',
            repairer_party: 'OURS',
          })
        ).data.data;
        caseId = opened.id;
      }
      if (!caseId) caseId = row.id;
      const preview = (await api.get(`/repair-cases/${caseId}/preview`)).data.data;
      queryClient.invalidateQueries({ queryKey: ['repair-cases'] });
      setReading({
        row: preview.case || row,
        loading: false,
        reports: [{ title: 'Repair report', html: preview.html || '' }],
        filed: !!preview.document_id,
      });
    } catch (err) {
      setReading({
        row,
        loading: false,
        reports: [],
        error: err.response?.data?.error || 'Could not open this job',
      });
    }
  }

  const caseList = (rows, empty) =>
    rows.length === 0 ? (
      <p className="text-sm text-gray-600">{empty}</p>
    ) : (
      <ul className="space-y-2">
        {rows.map((row) => (
          <li key={row.id}>
            <button
              type="button"
              className="text-left text-sm underline"
              onClick={() => openIssuedReports(row)}
            >
              {row.building_name || `Building ${row.building_id}`} · SS {row.ss_code || '—'} · {row.guide?.title || 'Repair'}
              {' · '}
              {statusLabel[row.status] || row.status || 'Open'}
            </button>
          </li>
        ))}
      </ul>
    );

  return (
    <div>
      <PageHeading
        title={section === 'list' ? 'Job list' : 'Repairing'}
        subPages={[
          { id: 'open', label: 'Open Job', active: section === 'open', onClick: () => setSection('open') },
          { id: 'list', label: 'Job list', active: section === 'list', onClick: () => setSection('list') },
        ]}
        infoTitle="Repairing = failed SS inspection track"
        infoChildren={
          <div className="space-y-2 text-sm">
            <p>
              Open Job is the failed-inspection queue. Job list opens the issued service report and re-inspection report for that job.
            </p>
          </div>
        }
      />

      {section === 'list' ? (
        <>
          <SectionCard title="In progress">
            {caseList(inProgressCases, 'No repair in progress.')}
          </SectionCard>
          <SectionCard title="Done">
            {caseList(doneCases, 'No finished repair yet.')}
          </SectionCard>
        </>
      ) : (
      <>
      {user?.role !== 'WORKER' && (
        <SectionCard
          title="Repair cases"
          actions={
            <button
              type="button"
              className="rounded bg-gray-900 px-3 py-1.5 text-sm text-white"
              onClick={() => openCase.mutate(newCase)}
            >
              Open case
            </button>
          }
        >
          <p className="text-sm text-gray-700">
            Choose who inspected and who repairs. Yesterday’s job from another company starts as their inspection and our repair.
          </p>
          <div className="grid gap-3 md:grid-cols-2">
            <label className="text-sm">
              Building
              <select
                className="mt-1 w-full rounded border border-green-300 px-3 py-2"
                value={newCase.building_id}
                onChange={(e) => setNewCase((prev) => ({ ...prev, building_id: e.target.value }))}
              >
                <option value="">Select</option>
                {buildings.map((building) => (
                  <option key={building.id} value={building.id}>
                    {building.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-sm">
              SS
              <input
                className="mt-1 w-full rounded border border-green-300 px-3 py-2"
                value={newCase.ss_code}
                onChange={(e) => setNewCase((prev) => ({ ...prev, ss_code: e.target.value }))}
              />
            </label>
            <label className="text-sm">
              Inspected by
              <select
                className="mt-1 w-full rounded border border-green-300 px-3 py-2"
                value={newCase.inspector_party}
                onChange={(e) => setNewCase((prev) => ({ ...prev, inspector_party: e.target.value }))}
              >
                <option value="OURS">Our company</option>
                <option value="EXTERNAL">Another company</option>
              </select>
            </label>
            <label className="text-sm">
              Repaired by
              <select
                className="mt-1 w-full rounded border border-green-300 px-3 py-2"
                value={newCase.repairer_party}
                onChange={(e) => setNewCase((prev) => ({ ...prev, repairer_party: e.target.value }))}
              >
                <option value="OURS">Our company</option>
                <option value="EXTERNAL">Another company</option>
              </select>
            </label>
          </div>
          {cases.length > 0 && (
            <ul className="space-y-1 text-sm">
              {cases.map((row) => (
                <li key={row.id}>
                  <button type="button" className="underline" onClick={() => openIssuedReports(row)}>
                    {row.building_name || `Building ${row.building_id}`} · SS {row.ss_code || '—'} · {row.guide?.title}
                  </button>
                  {' '}
                  <button type="button" className="text-gray-600 underline" onClick={() => navigate(`/repairing/cases/${row.id}`)}>
                    Edit
                  </button>
                </li>
              ))}
            </ul>
          )}
        </SectionCard>
      )}

      {user?.role !== 'WORKER' && (
        <div className="mb-6 p-4 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-950">
          <strong>Admin actions per row:</strong> Send Report · Request Quote · Open inspection (Complete when repair
          done).
        </div>
      )}

      {user?.role === 'WORKER' && pendingQuotes.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
          <h3 className="font-semibold text-amber-900 mb-2">Quote requests for you</h3>
          <div className="space-y-2">
            {pendingQuotes.map((q) => (
              <div
                key={q.id}
                className="flex items-center justify-between bg-white rounded-lg p-3 border border-amber-200"
              >
                <span className="text-sm">
                  {q.building_name} · {q.ss_code} {q.ss_name}
                </span>
                <button
                  onClick={() => setRespondingQuote(q)}
                  className="px-4 py-1 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm"
                >
                  Respond
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {sorted.length === 0 ? (
        <div className="bg-card rounded-lg shadow p-12 text-center">
          <Wrench className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500">No repairs required</p>
          <p className="text-sm text-gray-400 mt-1">
            Nothing in this queue yet. Failed inspections appear here when an inspection is marked Fail.
          </p>
        </div>
      ) : (
        <div className="bg-card rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700"
                  onClick={() => setSortKey('date')}
                >
                  Due Date
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700"
                  onClick={() => setSortKey('building')}
                >
                  Building
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700"
                  onClick={() => setSortKey('ss')}
                >
                  SS
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  IQP
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Inspection Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Report / Quote
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sorted.map((row) => {
                const style = getRowStyle(row);
                return (
                  <tr
                    key={`${row.job_id}-${row.inspection_id}`}
                    role="button"
                    tabIndex={0}
                    onClick={(e) => handleRowClick(e, row)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') handleRowClick(e, row);
                    }}
                    className={`cursor-pointer hover:opacity-90 ${style.row}`}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <Calendar className="w-4 h-4 text-gray-400 mr-2" />
                        <span className={`text-sm ${style.date}`}>
                          {row.due_date ? new Date(row.due_date).toLocaleDateString() : '—'}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <Building2 className="w-5 h-5 text-gray-400 mr-3 shrink-0" />
                        <span className="text-sm font-medium text-gray-900">{row.building_name || `Building #${row.building_id}`}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900">{row.ss_code}</span>
                      {row.ss_name && row.ss_name !== row.ss_code && (
                        <span className="text-gray-500 text-sm"> — {row.ss_name}</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <User className="w-4 h-4 text-gray-400 mr-2" />
                        <span className="text-sm text-gray-900">{row.assigned_worker_name || '—'}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded ${style.badge}`}>
                        {JOB_STATUS_LABELS[row.status] ?? row.status ?? 'FAIL'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {row.inspection_date ? new Date(row.inspection_date).toLocaleDateString() : '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div className="flex flex-col gap-1">
                        {row.report_deliveries?.length > 0 ? (
                          <span className="inline-flex items-center text-green-600">
                            <FileCheck className="w-4 h-4 mr-1" />
                            Report sent
                          </span>
                        ) : (
                          user?.role !== 'WORKER' && (
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); setSendReportsRow(row); }}
                              className="inline-flex items-center text-primary-600 hover:text-primary-900"
                            >
                              <Send className="w-4 h-4 mr-1" />
                              Send Report
                            </button>
                          )
                        )}
                        {row.quote_requests?.length > 0 ? (
                          <span className="inline-flex items-center text-gray-600 text-xs">
                            <ClipboardList className="w-3 h-3 mr-1" />
                            {row.quote_requests.some((q) => q.status === 'ACCEPTED') && 'Quote accepted'}
                            {row.quote_requests.some((q) => q.status === 'PENDING') && !row.quote_requests.some((q) => q.status === 'ACCEPTED') && 'Quote pending'}
                            {row.quote_requests.every((q) => q.status === 'REJECTED') && 'Quote rejected'}
                          </span>
                        ) : (
                          user?.role !== 'WORKER' && (
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); setQuoteRequestRow(row); }}
                              className="inline-flex items-center text-blue-600 hover:text-blue-900"
                            >
                              <FileText className="w-4 h-4 mr-1" />
                              Request Quote
                            </button>
                          )
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex flex-wrap items-center gap-3">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            openCase.mutate({
                              building_id: row.building_id,
                              job_id: row.job_id,
                              ss_code: row.ss_code,
                              inspector_party: 'OURS',
                              repairer_party: 'OURS',
                            });
                          }}
                          className="text-gray-900 underline"
                        >
                          Repair case
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/jobs/${row.job_id}`);
                          }}
                          className="text-amber-800 font-semibold hover:text-amber-950"
                        >
                          Open inspection &amp; finish
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/buildings/${row.building_id}#ss-progress`);
                          }}
                          className="text-primary-600 hover:text-primary-900"
                        >
                          Building SS
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      </>
      )}

      {reading ? (
        <SectionCard
          title={reading.row?.building_name ? `${reading.row.building_name} · SS ${reading.row.ss_code || '—'}` : 'Repair report'}
          actions={
            <button type="button" className="text-sm underline" onClick={() => setReading(null)}>
              Close
            </button>
          }
        >
          {reading.loading ? <p className="text-sm text-gray-700">Loading the report...</p> : null}
          {reading.error ? <p className="text-sm text-gray-800">{reading.error}</p> : null}
          {(reading.reports || []).map((report) => (
            <div key={report.title} className="space-y-2">
              <h3 className="text-sm font-semibold text-gray-900">{report.title}</h3>
              {report.html ? (
                <iframe title={report.title} className="h-[70vh] w-full rounded border border-green-300 bg-white" srcDoc={report.html} />
              ) : null}
            </div>
          ))}
          {reading.filed ? <p className="text-sm text-gray-700">Filed in this building’s Service report folder, and in Issued document.</p> : null}
        </SectionCard>
      ) : null}

      {sendReportsRow && (
        <SendReportsModal
          row={sendReportsRow}
          onClose={() => setSendReportsRow(null)}
        />
      )}
      {quoteRequestRow && (
        <QuoteRequestModal
          row={quoteRequestRow}
          onClose={() => setQuoteRequestRow(null)}
        />
      )}
      {respondingQuote && (
        <QuoteRequestResponseModal
          quoteRequest={respondingQuote}
          onClose={() => setRespondingQuote(null)}
        />
      )}
    </div>
  );
}

export default Repairing;
